import { BaseViewModel } from "./Base/BaseViewModel";
import { UserProfileViewModel } from "./UserProfileViewModel";
import { GetCorrelation, ICorrelation } from "./Utilities";
import { DailyEntryViewModel } from "./DailyEntryViewModel";
import { GetToday } from "../Services/Utilities";
import { PeriodType, RecapEntriesViewModel } from "./RecapEntriesViewModel";
import { DataServices } from "../Services/DataService/DataService";
import { DailyEntry, DataEntityBase } from "../Services/DataService/Model";
import { RecapEntryProperties, RecapEntryType } from "../Services/DataService/Model/RecapEntry";
import { GetAuthServices } from "../Services/AuthService/AuthService";
import { TutorialStateViewModel } from "./TutorialStateViewModel";
import { ScoreTrendViewModel } from "./ScoreTrendViewModel";
import { FeedbackViewModel } from "./FeedbackViewModel";
import { appInsights } from "../Services/AppInsightsService";
import { BasicEntityWatcher } from "../Services/DataService/EntityWatcher";
import { IConstructorOptions } from "../Utilities";



export class JournalViewModel extends BaseViewModel {

    public static async GetModel() {

        //
        // Make sure that we only initialize once
        //
        if (this._inititalizePromise) {
            await this._inititalizePromise;
        }

        //
        // Now that we are here, we can safely check if the model is already initialized
        //
        if (!this._journalViewModel) {
            var retVal = new JournalViewModel();
            this._journalViewModel = retVal;
            this._inititalizePromise = retVal.Hydrate(GetCorrelation());

            GetAuthServices().then(async (authServices) => {
                authServices.userChanged.Subscribe(() => {

                    if (!authServices.IsLoggedIn())
                        JournalViewModel._journalViewModel = undefined;
                });
            });

            await this._inititalizePromise;
        }
        return this._journalViewModel;
    }


    public get UserProfileViewModel() {
        return this.userProfileViewModel!;
    }

    public get TodaysDailyEntry() {
        return this.todaysDailyEntry!;
    }

    public get WeeklyRecap() {
        return this.weeklyRecap!;
    }

    public get AllWeeklyRecaps() {
        return this.allWeeklyRecaps;
    }


    public get TutorialState() {
        return this.tutorialStateVM!;
    }

    public get ScoreTrendViewModel() {
        return this.scoreTrendVM!;
    }

    public get FeedbackViewModel() {
        return this.feedbackViewModel!;
    }

    protected onReplacedData(_oldData?: DataEntityBase, _newData?: DataEntityBase) {

        this.userProfileViewModel = undefined;
        this.todaysDailyEntry = undefined;
        this.tutorialStateVM = undefined;
        this.weeklyRecap = undefined;
        this.allWeeklyRecaps = [];
        this.scoreTrendVM = undefined;
        this.feedbackViewModel = undefined;

        this.Hydrate(GetCorrelation(), true);
    }

    protected async doHydrate(correlation: ICorrelation) {

        //
        // All the properties that get created and hydrated here should be 
        // cleared and set to undefined in onReplaceData
        //

        appInsights.trackEvent({ name: 'JournalViewModel.doHydrate.start' });

        appInsights.startTrackEvent('JournalViewModel.doHydrate.userProfilePromise');
        var userProfilePromise = (async () => {
            this.userProfileViewModel = await UserProfileViewModel.GetModel();;
            await this.userProfileViewModel.Hydrate(correlation);
        })();

        await Promise.all([userProfilePromise]);
        appInsights.stopTrackEvent('JournalViewModel.doHydrate.userProfilePromise');



        appInsights.startTrackEvent('JournalViewModel.doHydrate.otherPromises'); {

            var dailyEntryPromise = (async () => {
                appInsights.startTrackEvent('JournalViewModel.doHydrate.dailyEntryPromise');
                var today = GetToday();
                this.todaysDailyEntry = await DailyEntryViewModel.Manager.GetOrCreateByKey(correlation, today.toISOString(), DailyEntry.OptionsFromDate(today));
                await this.todaysDailyEntry.Hydrate(correlation);
                appInsights.stopTrackEvent('JournalViewModel.doHydrate.dailyEntryPromise');
            })();

            var tutorialPromise = (async () => {
                appInsights.startTrackEvent('JournalViewModel.doHydrate.tutorialPromise');
                this.tutorialStateVM = await TutorialStateViewModel.Manager.GetOrCreateByKey(correlation, TutorialStateViewModel.CreateId(), TutorialStateViewModel.Options());
                this.tutorialStateVM!.setJournalViewModel(this);
                await this.tutorialStateVM.Hydrate(correlation);
                appInsights.stopTrackEvent('JournalViewModel.doHydrate.tutorialPromise');
            })();


            var weeklyRecapPromise = (async () => {
                //
                // Prepare to get or create
                //
                appInsights.startTrackEvent('JournalViewModel.weeklyRecapPromise');
                var key = RecapEntriesViewModel.CreateKey(GetToday(), RecapEntryType.Week);
                var periodDates = RecapEntriesViewModel.GetPeriodDates(RecapEntryType.Week, GetToday(), PeriodType.Current);

                //
                // Create the options
                //
                // appInsights.trackEvent({ name: 'JournalViewModel.weeklyRecapPromise.optionsBuilder', properties: { key: key, periodDates: periodDates } });
                var optionsBuilder = DataEntityBase.ConstructorOptionsBuilder();
                const id = RecapEntriesViewModel.CreateIdFromDate(GetToday(), RecapEntryType.Week);
                optionsBuilder.addId(id);
                optionsBuilder.addProp(RecapEntryProperties.endDate, periodDates.end);
                optionsBuilder.addProp(RecapEntryProperties.startDate, periodDates.start);
                optionsBuilder.addProp(RecapEntryProperties.entryType, RecapEntryType.Week);
                var options = optionsBuilder.construct();

                // Get or create the recap
                //
                // appInsights.trackEvent({ name: 'JournalViewModel.weeklyRecapPromise.getOrCreate', properties: { key: key, options: options } });
                this.weeklyRecap = await RecapEntriesViewModel.Manager.GetOrCreateByKey(correlation, key, options);
                await this.weeklyRecap.Hydrate(correlation);


                appInsights.stopTrackEvent('JournalViewModel.weeklyRecapPromise');
            })();

            var allRecapsPromise = (async () => {

                appInsights.startTrackEvent('JournalViewModel.allRecapsPromise');
                await RecapEntriesViewModel.Initialize();

                RecapEntriesViewModel.Manager.EntitiesChanged.Subscribe(this.updateWeeklyRecaps);
                this.allWeeklyRecaps = [];
                await this.updateWeeklyRecaps();
                appInsights.stopTrackEvent('JournalViewModel.allRecapsPromise');
            })();



            var scoreTrendPromise = (async () => {
                appInsights.startTrackEvent('JournalViewModel.scoreTrendPromise');
                let model = ScoreTrendViewModel.GetModel();
                await model.Hydrate(correlation);
                this.scoreTrendVM = model;
                appInsights.stopTrackEvent('JournalViewModel.scoreTrendPromise');
            })();

            var feedbackPromise = (async () => {
                appInsights.startTrackEvent('JournalViewModel.feedbackPromise');
                this.feedbackViewModel = await FeedbackViewModel.GetModel();
                await this.feedbackViewModel.Hydrate(correlation);
                appInsights.stopTrackEvent('JournalViewModel.feedbackPromise');
            })();


            await Promise.all([
                dailyEntryPromise,
                weeklyRecapPromise,
                tutorialPromise,
                scoreTrendPromise,
                allRecapsPromise,
                feedbackPromise
            ]);
        }
        appInsights.stopTrackEvent('JournalViewModel.doHydrate.otherPromises');
    }


    private updateWeeklyRecaps = async () => {

        var allRecaps = await RecapEntriesViewModel.Manager.GetAllItems(GetCorrelation());
        var weeklyRecaps = (
            allRecaps.filter(x => x.TypedData.entryType === RecapEntryType.Week)
                .sort((a, b) => b.TypedData.DateRange.end.getTime() - a.TypedData.DateRange.end.getTime())
                .slice(0, 10) // magic number to prevent too many items from being returned
        );

        var changed = false;
        if (this.allWeeklyRecaps.length !== weeklyRecaps.length) {
            changed = true;
        } else {
            let missed = this.AllWeeklyRecaps.filter((x) => !weeklyRecaps.includes(x));
            if (missed.length > 0) {
                changed = true;
            }
        }



        if (changed) {

            this.allWeeklyRecaps = weeklyRecaps;
            const itemsToUnregistere = this.allWeeklyRecaps.filter((entry) => !weeklyRecaps.includes(entry));
            const itemsToRegister = weeklyRecaps.filter((entry) => !this.allWeeklyRecaps.includes(entry));

            for (let item of itemsToUnregistere) {
                item.PropertyChanged.Unsubscribe(this.updateWeeklyRecaps);
            }
            for (let item of itemsToRegister) {
                item.PropertyChanged.Subscribe(this.updateWeeklyRecaps);
            }

            const correlation = GetCorrelation();
            await Promise.all(this.allWeeklyRecaps.map(async (x) => x.Hydrate(correlation)));
            this.FireChanged('AllWeeklyRecaps');
        }
    }

    protected CreateData(_watcher: BasicEntityWatcher, _options?: IConstructorOptions): DataEntityBase {
        return {} as any;
    }

    private constructor() {
        super();

        DataServices.Raw.cachedItemChanged.Subscribe(() => this.onReplacedData(undefined, undefined));
    }

    private static _journalViewModel: JournalViewModel | undefined = undefined;
    private static _inititalizePromise: Promise<void> | undefined = undefined;
    private userProfileViewModel?: UserProfileViewModel = undefined;
    private todaysDailyEntry?: DailyEntryViewModel = undefined;
    private weeklyRecap?: RecapEntriesViewModel = undefined;
    private allWeeklyRecaps: RecapEntriesViewModel[] = [];
    private tutorialStateVM?: TutorialStateViewModel = undefined;
    private scoreTrendVM?: ScoreTrendViewModel = undefined;
    private feedbackViewModel?: FeedbackViewModel = undefined;

}